import { useContext } from "react";
import { PermissionContext } from "../context/PermissionContext";

/**
 * Hook for checking a single permission. Returns true if the current
 * user is elevated OR has the named permission.
 *
 * Usage:
 *   const canEditPrograms = useHasPermission("manage_programs");
 *   if (!canEditPrograms) return <FallbackUI />;
 *
 * NOTE: This is purely a UX optimization. The backend route gate is the
 * actual security boundary — never trust this hook to enforce access.
 * Use it to hide buttons / disable inputs / route-guard pages so users
 * don't bump into unexpected 403s from the backend.
 */
export function useHasPermission(name: string): boolean {
  const { hasPermission } = useContext(PermissionContext);
  return hasPermission(name);
}

/**
 * Hook to check multiple permissions at once. Returns a record keyed by
 * permission name. Useful when a component conditionally shows several
 * actions and you want a single re-render path.
 */
export function useHasPermissions(...names: string[]): Record<string, boolean> {
  const { hasPermission } = useContext(PermissionContext);
  return names.reduce<Record<string, boolean>>((acc, name) => {
    acc[name] = hasPermission(name);
    return acc;
  }, {});
}

/**
 * A LAYERED permission: one the server checks ON TOP of an older, broader
 * permission (PM 9123a167: view_/manage_member_payment_methods on top of
 * process_payments or manage_billing, send_member_message on top of
 * send_email or send_sms).
 *
 * True when the caller holds `base` AND either holds `layered` or `layered`
 * is not defined in this club yet (its migration has not run there). That is
 * exactly when the API lets the request through, so the UI never hides a
 * control the API allows. Elevated callers pass, as on the server.
 */
export function useHasLayeredPermission(base: string, layered: string): boolean {
  const { hasPermission, capabilities } = useContext(PermissionContext);
  if (!hasPermission(base)) return false;
  const notDefinedHere = (capabilities?.undefined_permissions || []).includes(layered);
  return notDefinedHere || hasPermission(layered);
}

/**
 * Hook returning the full permission context (loading state, isElevated
 * flag, refresh fn). Use the simpler useHasPermission hook unless you
 * need the loading flag (e.g., to delay a render until perms are known).
 */
export function usePermissions() {
  return useContext(PermissionContext);
}
